import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../api/http'
import { rpc, useRpcEvent } from '../api/rpc'
import { Button } from '../components/ui/Button'
import { Badge, EmptyState, type BadgeTone } from '../components/ui/Card'
import { Field, Input, Textarea } from '../components/ui/Field'
import { Select } from '../components/ui/Select'
import { Modal } from '../components/ui/Modal'
import { useToast } from '../components/ui/Toast'
import { confirmDialog, promptDialog } from '../components/ui/Confirm'
import { Count, ModalFooter, Row, RowMenu, Table, Td, Th, Toolbar } from '../components/ui/DataTable'
import { useWorkspaces, WorkspacePicker } from '../components/useWorkspaces'
import { formatDateTime } from '../lib/format'
import { PanelHead } from './KnowledgePage'

type Status = 'pending' | 'indexing' | 'ready' | 'error'
type SourceKind = 'gdrive' | 'url'
interface Collection { id: string; workspaceId: string; name: string; description: string; documentCount: number; chunkCount: number }
interface Doc { id: string; collectionId: string; source: string; path: string; title: string; status: Status; error: string | null; chunkCount: number; updatedAt: number }
interface Source {
  id: string
  collectionId: string
  kind: SourceKind
  ref: string
  title: string
  intervalHours: number
  status: 'idle' | 'syncing' | 'error'
  lastSyncAt: number | null
  lastError: string | null
  documentCount: number
  createdBy: string | null
  createdAt: number
}
interface SyncResult {
  added: number
  updated: number
  removed: number
  skipped: number
  errors: string[]
}
interface Hit {
  n: number
  ref: string
  title: string
  collection: string
  headingPath: string
  page: number | null
  text: string
  score: number
  keywordRank: number | null
  semanticRank: number | null
}
interface Draft { id?: string; name: string; description: string }
interface EvalCase { id: string; query: string; expectedDocumentId: string; expectedTitle: string; createdAt: number }
interface EvalRun {
  id: string
  mode: 'hybrid' | 'keyword'
  cases: number
  hitAt3: number
  hitAt8: number
  mrr: number
  details: { caseId: string; query: string; expectedDocumentId: string; rank: number | null }[]
  createdAt: number
}
interface WeakReport {
  days: number
  searches: number
  weakSearches: number
  items: { query: string; asked: number; weak: number; people: number; lastAt: number }[]
}

const STATUS_TONE: Record<Status, BadgeTone> = { pending: 'neutral', indexing: 'accent', ready: 'success', error: 'danger' }
const SOURCE_STATUS_TONE: Record<Source['status'], BadgeTone> = { idle: 'success', syncing: 'accent', error: 'danger' }
const INTERVALS = [0, 6, 24, 168] as const

export function KnowledgeBase() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { items: workspaces } = useWorkspaces()
  const [ws, setWs] = useState('')
  const [collections, setCollections] = useState<Collection[] | null>(null)
  const [current, setCurrent] = useState('')
  const [docs, setDocs] = useState<Doc[] | null>(null)
  const [sources, setSources] = useState<Source[] | null>(null)
  const [sourceModalKind, setSourceModalKind] = useState<SourceKind | null>(null)
  const [sourceRef, setSourceRef] = useState('')
  const [sourceInterval, setSourceInterval] = useState('24')
  const [submittingSource, setSubmittingSource] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [uploading, setUploading] = useState(false)
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<Hit[] | null>(null)
  const [mode, setMode] = useState<'hybrid' | 'keyword' | null>(null)
  const [evalCases, setEvalCases] = useState<EvalCase[] | null>(null)
  const [evalLastRun, setEvalLastRun] = useState<EvalRun | null>(null)
  const [evalModalOpen, setEvalModalOpen] = useState(false)
  const [evalCaseQuery, setEvalCaseQuery] = useState('')
  const [evalCaseDocId, setEvalCaseDocId] = useState('')
  const [submittingEval, setSubmittingEval] = useState(false)
  const [runningEvals, setRunningEvals] = useState(false)
  const [weakDays, setWeakDays] = useState('30')
  const [weakReport, setWeakReport] = useState<WeakReport | null>(null)
  const picker = useRef<HTMLInputElement>(null)
  const readOnly = workspaces.find(w => w.id === ws)?.role === 'viewer'
  const fail = useCallback((err: unknown) => toast((err as Error).message, 'error'), [toast])

  const loadCollections = useCallback(() => rpc.call<{ items: Collection[] }>('kb.collections.list', ws ? { workspaceId: ws } : {})
    .then(r => {
      setCollections(r.items)
      setCurrent(c => (r.items.some(x => x.id === c) ? c : r.items[0]?.id ?? ''))
    }).catch(fail), [ws, fail])
  const loadDocs = useCallback(() => {
    if (!current) { setDocs(null); return }
    void rpc.call<{ items: Doc[] }>('kb.documents.list', { collectionId: current }).then(r => setDocs(r.items)).catch(fail)
  }, [current, fail])
  const loadSources = useCallback(() => {
    if (!current) { setSources(null); return }
    void rpc.call<{ items: Source[] }>('kb.sources.list', { collectionId: current }).then(r => setSources(r.items)).catch(fail)
  }, [current, fail])
  const loadEvals = useCallback(() => {
    if (!current) { setEvalCases(null); setEvalLastRun(null); return }
    void rpc.call<{ cases: EvalCase[]; lastRun: EvalRun | null }>('kb.evals.list', { collectionId: current })
      .then(r => {
        setEvalCases(r.cases)
        setEvalLastRun(r.lastRun)
      }).catch(fail)
  }, [current, fail])
  const loadWeakReport = useCallback(() => {
    if (readOnly) { setWeakReport(null); return }
    void rpc.call<WeakReport>('kb.insights.weakQueries', { ...(ws ? { workspaceId: ws } : {}), days: Number(weakDays) })
      .then(r => setWeakReport(r))
      .catch(fail)
  }, [ws, weakDays, readOnly, fail])

  useEffect(() => { void loadCollections(); setHits(null); setMode(null) }, [loadCollections])
  useEffect(() => { loadDocs(); loadSources(); loadEvals() }, [loadDocs, loadSources, loadEvals])
  useEffect(() => { loadWeakReport() }, [loadWeakReport])
  useRpcEvent('kb.document', p => {
    const d = p as Doc
    if (d.collectionId !== current) return
    setDocs(list => (list?.some(x => x.id === d.id) ? list.map(x => (x.id === d.id ? d : x)) : [...(list ?? []), d]))
    if (d.status === 'ready' || d.status === 'error') void loadCollections()
  })
  useRpcEvent('kb.source', p => {
    const s = p as Source | { id: string; collectionId: string; deleted: true }
    if (s.collectionId !== current) return
    setSources(list => {
      if ('deleted' in s && s.deleted) return list ? list.filter(x => x.id !== s.id) : null
      const updated = s as Source
      if (!list) return [updated]
      return list.some(x => x.id === updated.id) ? list.map(x => (x.id === updated.id ? updated : x)) : [...list, updated]
    })
    loadDocs()
    void loadCollections()
  })

  const col = collections?.find(c => c.id === current)
  const save = async () => {
    if (!draft) return
    try {
      const r = draft.id
        ? await rpc.call<Collection>('kb.collections.update', { collectionId: draft.id, name: draft.name, description: draft.description })
        : await rpc.call<Collection>('kb.collections.create', { ...(ws ? { workspaceId: ws } : {}), name: draft.name, description: draft.description })
      setDraft(null)
      setCurrent(r.id)
      void loadCollections()
    } catch (err) { fail(err) }
  }
  const removeCollection = async () => {
    if (!col || !await confirmDialog(t('kb.confirmDeleteCollection', { name: col.name }), { danger: true })) return
    try { await rpc.call('kb.collections.delete', { collectionId: col.id }); void loadCollections() } catch (err) { fail(err) }
  }
  const upload = async (files: FileList | null) => {
    if (!files?.length || !current) return
    setUploading(true)
    try {
      for (const f of Array.from(files)) {
        const form = new FormData()
        form.append('file', f, f.name)
        await api.post(`/api/kb/${encodeURIComponent(current)}/documents`, form)
      }
      toast(t('kb.uploaded'), 'success')
    } catch (err) { fail(err) } finally {
      setUploading(false)
      if (picker.current) picker.current.value = ''
      loadDocs()
    }
  }
  const addFromWorkspace = async () => {
    const text = await promptDialog(t('kb.addFromWorkspacePrompt'))
    const paths = text?.split(',').map(s => s.trim()).filter(Boolean) ?? []
    if (!paths.length) return
    try { await rpc.call('kb.documents.addPaths', { collectionId: current, paths }); loadDocs() } catch (err) { fail(err) }
  }
  const removeDoc = async (d: Doc) => {
    if (!await confirmDialog(t('kb.confirmDeleteDocument', { name: d.title }), { danger: true })) return
    try { await rpc.call('kb.documents.delete', { documentId: d.id }); loadDocs(); void loadCollections() } catch (err) { fail(err) }
  }
  const intervalLabel = (h: number) => (h === 0 || h === 6 || h === 24 || h === 168 ? t(`kb.interval_${h}`) : `${h}h`)
  const openAddSource = (kind: SourceKind) => {
    setSourceModalKind(kind)
    setSourceRef('')
    setSourceInterval('24')
  }
  const addSource = async () => {
    if (!sourceModalKind || !current || !sourceRef.trim()) return
    setSubmittingSource(true)
    try {
      const res = await rpc.call<{ source: Source; result: SyncResult }>('kb.sources.add', {
        collectionId: current,
        kind: sourceModalKind,
        ref: sourceRef.trim(),
        intervalHours: Number(sourceInterval)
      })
      if (res.result.errors?.length) {
        toast(res.result.errors[0], 'error')
      } else {
        toast(t('kb.syncSummary', { added: res.result.added, updated: res.result.updated, removed: res.result.removed, skipped: res.result.skipped }), 'success')
      }
      setSourceModalKind(null)
      setSourceRef('')
      setSourceInterval('24')
      loadSources()
      loadDocs()
      void loadCollections()
    } catch (err) { fail(err) } finally {
      setSubmittingSource(false)
    }
  }
  const syncSource = async (s: Source) => {
    try {
      const res = await rpc.call<SyncResult>('kb.sources.sync', { sourceId: s.id })
      if (res.errors?.length) {
        toast(res.errors[0], 'error')
      } else {
        toast(t('kb.syncSummary', { added: res.added, updated: res.updated, removed: res.removed, skipped: res.skipped }), 'success')
      }
      loadSources()
      loadDocs()
      void loadCollections()
    } catch (err) { fail(err) }
  }
  const updateSourceInterval = async (s: Source, intervalHours: number) => {
    try {
      await rpc.call<Source>('kb.sources.update', { sourceId: s.id, intervalHours })
      loadSources()
    } catch (err) { fail(err) }
  }
  const removeSource = async (s: Source) => {
    if (!await confirmDialog(t('kb.confirmDeleteSource', { name: s.title, count: s.documentCount }), { danger: true })) return
    try {
      await rpc.call('kb.sources.delete', { sourceId: s.id })
      loadSources()
      loadDocs()
      void loadCollections()
    } catch (err) { fail(err) }
  }
  const openAddEval = (initialQuery = '') => {
    const readyDocs = docs?.filter(d => d.status === 'ready') ?? []
    setEvalCaseQuery(initialQuery)
    setEvalCaseDocId(readyDocs[0]?.id ?? '')
    setEvalModalOpen(true)
  }
  const addEvalCase = async () => {
    if (!current || !evalCaseQuery.trim() || !evalCaseDocId) return
    setSubmittingEval(true)
    try {
      await rpc.call<EvalCase>('kb.evals.add', { collectionId: current, query: evalCaseQuery.trim(), documentId: evalCaseDocId })
      setEvalModalOpen(false)
      setEvalCaseQuery('')
      setEvalCaseDocId('')
      loadEvals()
    } catch (err) { fail(err) } finally {
      setSubmittingEval(false)
    }
  }
  const deleteEval = async (c: EvalCase) => {
    if (!await confirmDialog(t('kb.confirmDeleteEval'), { danger: true })) return
    try {
      await rpc.call('kb.evals.delete', { caseId: c.id })
      loadEvals()
    } catch (err) { fail(err) }
  }
  const runEvals = async () => {
    if (!current) return
    setRunningEvals(true)
    try {
      const res = await rpc.call<EvalRun>('kb.evals.run', { collectionId: current })
      setEvalLastRun(res)
      loadEvals()
    } catch (err) { fail(err) } finally {
      setRunningEvals(false)
    }
  }
  const search = async () => {
    if (!query.trim()) return
    try {
      const r = await rpc.call<{ items: Hit[]; mode: 'hybrid' | 'keyword' }>('kb.search', { query, ...(ws ? { workspaceId: ws } : {}) })
      setHits(r.items)
      setMode(r.mode)
    } catch (err) { fail(err) }
  }

  return (
    <div className="space-y-5"
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); if (!readOnly && current) void upload(e.dataTransfer.files) }}>
      <PanelHead
        eyebrow={`${t('settings.group.workspace')} / ${t('settings.nav.knowledge')}`}
        title={t('kb.title')}
        lede={t('kb.lede')}
        status={<>
          <b>{t('kb.docCount', { count: col?.documentCount ?? 0 })}</b>
          <span className="text-ink-500">·</span>
          <b>{t('kb.chunkCount', { count: col?.chunkCount ?? 0 })}</b>
          {readOnly && <><span className="text-ink-500">·</span><span>{t('kb.readOnly')}</span></>}
        </>}
        action={!readOnly ? <Button onClick={() => setDraft({ name: '', description: '' })}>{t('kb.newCollection')}</Button> : undefined}
      />
      <Toolbar>
        <WorkspacePicker items={workspaces} value={ws} onChange={setWs} />
        {collections && collections.length > 0 && (
          <Select label={t('kb.collection')} value={current} onChange={setCurrent}
            options={collections.map(c => ({ value: c.id, label: c.name }))} />
        )}
        {col && !readOnly && <>
          <Button variant="secondary" onClick={() => setDraft({ id: col.id, name: col.name, description: col.description })}>{t('kb.editCollection')}</Button>
          <Button variant="secondary" onClick={() => void removeCollection()}>{t('kb.deleteCollection')}</Button>
        </>}
      </Toolbar>

      {collections?.length === 0 ? (
        <EmptyState title={t('kb.noCollections')} body={t('kb.noCollectionsBody')} />
      ) : col && (
        <>
          <p className="text-sm text-ink-400">{col.description}</p>
          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              <input ref={picker} type="file" multiple hidden accept=".md,.markdown,.txt,.html,.htm,.pdf,.docx,.xlsx,.csv" onChange={e => void upload(e.target.files)} />
              <Button loading={uploading} onClick={() => picker.current?.click()}>{t('kb.upload')}</Button>
              <Button variant="secondary" onClick={() => void addFromWorkspace()}>{t('kb.addFromWorkspace')}</Button>
            </div>
          )}
          {docs?.length === 0 ? (
            <EmptyState title={t('kb.noDocuments')} body={t('kb.noDocumentsBody')} />
          ) : (
            <Table footer={<Count>{t('kb.docCount', { count: docs?.length ?? 0 })}</Count>}>
              <thead>
                <tr>
                  <Th rail />
                  <Th>{t('kb.colTitle')}</Th>
                  <Th>{t('kb.colStatus')}</Th>
                  <Th>{t('kb.colChunks')}</Th>
                  <Th>{t('kb.colSource')}</Th>
                  <Th>{t('kb.colUpdated')}</Th>
                  <Th><span className="sr-only">{t('common.actions')}</span></Th>
                </tr>
              </thead>
              <tbody>
                {(docs ?? []).map(d => (
                  <Row key={d.id} rail={d.status === 'error' ? 'attention' : d.status === 'ready' ? 'ok' : 'idle'}>
                    <Td><div className="font-semibold">{d.title}</div><div className="font-mono text-[11.5px] text-ink-500">{d.path}</div></Td>
                    <Td>
                      <Badge tone={STATUS_TONE[d.status]}>{t(`kb.status_${d.status}`)}</Badge>
                      {d.error && <div className="mt-1 text-xs text-red-700">{d.error}</div>}
                    </Td>
                    <Td className="text-ink-500">{d.chunkCount}</Td>
                    <Td className="text-ink-500">{t(`kb.source_${d.source}`)}</Td>
                    <Td className="text-ink-500">{formatDateTime(d.updatedAt, i18n.language)}</Td>
                    <Td>{!readOnly && <RowMenu items={[
                      { label: t('kb.reindex'), onSelect: () => void rpc.call('kb.documents.reindex', { documentId: d.id }).catch(fail) },
                      { label: t('common.delete'), danger: true, divider: true, onSelect: () => void removeDoc(d) }
                    ]} />}</Td>
                  </Row>
                ))}
              </tbody>
            </Table>
          )}

          <section className="space-y-3 rounded-2xl border border-line bg-cream-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-bold">{t('kb.sources')}</h2>
                <p className="text-sm text-ink-400">{t('kb.sourcesHint')}</p>
              </div>
              {!readOnly && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => openAddSource('gdrive')}>{t('kb.addDrive')}</Button>
                  <Button variant="secondary" onClick={() => openAddSource('url')}>{t('kb.addUrl')}</Button>
                </div>
              )}
            </div>

            {sources?.length === 0 ? (
              <p className="text-sm text-ink-400">{t('kb.noSources')}</p>
            ) : (
              <Table footer={<Count>{sources?.length ?? 0}</Count>}>
                <thead>
                  <tr>
                    <Th rail />
                    <Th>{t('kb.colSource')}</Th>
                    <Th>{t('kb.colTitle')}</Th>
                    <Th>{t('kb.interval')}</Th>
                    <Th>{t('kb.colUpdated')}</Th>
                    <Th>{t('kb.colStatus')}</Th>
                    <Th><span className="sr-only">{t('common.actions')}</span></Th>
                  </tr>
                </thead>
                <tbody>
                  {(sources ?? []).map(s => (
                    <Row key={s.id} rail={s.status === 'error' ? 'error' : s.status === 'syncing' ? 'attention' : 'ok'}>
                      <Td><Badge>{t(`kb.source_kind_${s.kind}`)}</Badge></Td>
                      <Td>
                        <div className="font-semibold">{s.title}</div>
                        <div className="max-w-xs truncate font-mono text-[11.5px] text-ink-500 sm:max-w-sm md:max-w-md">{s.ref}</div>
                      </Td>
                      <Td className="text-ink-500">{intervalLabel(s.intervalHours)}</Td>
                      <Td className="text-ink-500">
                        {s.lastSyncAt ? t('kb.lastSync', { when: formatDateTime(s.lastSyncAt, i18n.language) }) : t('kb.neverSynced')}
                      </Td>
                      <Td>
                        <Badge tone={SOURCE_STATUS_TONE[s.status]}>{t(`kb.sourceStatus_${s.status}`)}</Badge>
                        {s.lastError && <div className="mt-1 max-w-xs text-xs text-red-700">{s.lastError}</div>}
                      </Td>
                      <Td>
                        {!readOnly && (
                          <RowMenu items={[
                            { label: t('kb.syncNow'), onSelect: () => void syncSource(s) },
                            ...INTERVALS.map((h, i) => ({
                              label: `${t('kb.interval')}: ${intervalLabel(h)}`,
                              disabled: s.intervalHours === h,
                              divider: i === 0,
                              onSelect: () => void updateSourceInterval(s, h)
                            })),
                            { label: t('common.delete'), danger: true, divider: true, onSelect: () => void removeSource(s) }
                          ]} />
                        )}
                      </Td>
                    </Row>
                  ))}
                </tbody>
              </Table>
            )}
          </section>

          <section className="space-y-3 rounded-2xl border border-line bg-cream-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-bold">{t('kb.evalTitle')}</h2>
                <p className="text-sm text-ink-400">{t('kb.evalHint')}</p>
              </div>
              {!readOnly && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => openAddEval()}>{t('kb.evalAdd')}</Button>
                  <Button
                    variant="secondary"
                    loading={runningEvals}
                    disabled={!evalCases?.length}
                    onClick={() => void runEvals()}
                  >
                    {t('kb.evalRun')}
                  </Button>
                </div>
              )}
            </div>

            {evalLastRun && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
                <span className="font-semibold text-ink-100">
                  {t('kb.evalScore', { hit3: evalLastRun.hitAt3, hit8: evalLastRun.hitAt8, cases: evalLastRun.cases, mrr: evalLastRun.mrr })}
                </span>
                <span className="text-ink-500">·</span>
                <span>
                  {t('kb.evalLastRun', {
                    when: formatDateTime(evalLastRun.createdAt, i18n.language),
                    mode: t(`kb.mode_${evalLastRun.mode}`)
                  })}
                </span>
              </div>
            )}

            {evalCases?.length === 0 ? (
              <p className="text-sm text-ink-400">{t('kb.evalEmpty')}</p>
            ) : (
              <Table footer={<Count>{evalCases?.length ?? 0}</Count>}>
                <thead>
                  <tr>
                    <Th rail />
                    <Th>{t('kb.evalQuestion')}</Th>
                    <Th>{t('kb.evalExpected')}</Th>
                    <Th>{t('kb.colStatus')}</Th>
                    {!readOnly && <Th><span className="sr-only">{t('common.actions')}</span></Th>}
                  </tr>
                </thead>
                <tbody>
                  {(evalCases ?? []).map(c => {
                    const detail = evalLastRun?.details.find(d => d.caseId === c.id)
                    const hasRank = detail && detail.rank !== null
                    return (
                      <Row key={c.id} rail={detail ? (hasRank ? 'ok' : 'error') : 'idle'}>
                        <Td><div className="font-semibold">{c.query}</div></Td>
                        <Td className="text-ink-500">{c.expectedTitle}</Td>
                        <Td>
                          {detail ? (
                            hasRank ? (
                              <Badge tone="success">{t('kb.evalRank', { rank: detail.rank })}</Badge>
                            ) : (
                              <span className="text-xs font-semibold text-red-700">{t('kb.evalMissing')}</span>
                            )
                          ) : (
                            <span className="text-xs text-ink-400">—</span>
                          )}
                        </Td>
                        {!readOnly && (
                          <Td>
                            <RowMenu items={[
                              { label: t('common.delete'), danger: true, onSelect: () => void deleteEval(c) }
                            ]} />
                          </Td>
                        )}
                      </Row>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </section>
        </>
      )}

      {!readOnly && (
        <section className="space-y-3 rounded-2xl border border-line bg-cream-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold">{t('kb.weakTitle')}</h2>
              <p className="text-sm text-ink-400">{t('kb.weakHint', { days: weakDays })}</p>
            </div>
            <div className="w-36">
              <Select
                aria-label={t('kb.period')}
                value={weakDays}
                onChange={setWeakDays}
                options={[
                  { value: '7', label: t('kb.days_7') },
                  { value: '30', label: t('kb.days_30') },
                  { value: '90', label: t('kb.days_90') }
                ]}
              />
            </div>
          </div>

          {weakReport && (
            <p className="text-xs font-semibold text-ink-500">
              {t('kb.weakSummary', { weak: weakReport.weakSearches, searches: weakReport.searches })}
            </p>
          )}

          {weakReport?.items.length === 0 ? (
            <p className="text-sm text-ink-400">{t('kb.weakEmpty')}</p>
          ) : (
            <Table footer={<Count>{weakReport?.items.length ?? 0}</Count>}>
              <thead>
                <tr>
                  <Th rail />
                  <Th>{t('kb.colQuestion')}</Th>
                  <Th>{t('kb.colAsked')}</Th>
                  <Th>{t('kb.colWeak')}</Th>
                  <Th>{t('kb.colPeople')}</Th>
                  <Th>{t('kb.colLastAsked')}</Th>
                  {col && <Th><span className="sr-only">{t('common.actions')}</span></Th>}
                </tr>
              </thead>
              <tbody>
                {(weakReport?.items ?? []).map((item, idx) => (
                  <Row key={idx} rail={item.weak > 0 ? 'attention' : 'idle'}>
                    <Td><div className="font-semibold">{item.query}</div></Td>
                    <Td className="text-ink-500">{item.asked}</Td>
                    <Td className="text-ink-500">{item.weak}</Td>
                    <Td className="text-ink-500">{item.people}</Td>
                    <Td className="text-ink-500">{formatDateTime(item.lastAt, i18n.language)}</Td>
                    {col && (
                      <Td>
                        <Button variant="secondary" size="sm" onClick={() => openAddEval(item.query)}>
                          {t('kb.makeCheck')}
                        </Button>
                      </Td>
                    )}
                  </Row>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      <section className="space-y-3 rounded-2xl border border-line bg-cream-50 p-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display text-lg font-bold">{t('kb.tryQuery')}</h2>
            {mode && <Badge>{t(`kb.mode_${mode}`)}</Badge>}
          </div>
          <p className="text-sm text-ink-400">{t('kb.tryQueryHint')}</p>
        </div>
        <form className="flex gap-2" onSubmit={e => { e.preventDefault(); void search() }}>
          <Input aria-label={t('kb.tryQuery')} value={query} placeholder={t('kb.queryPlaceholder')} onChange={e => setQuery(e.target.value)} />
          <Button type="submit" disabled={!query.trim()}>{t('kb.search')}</Button>
        </form>
        {hits?.length === 0 && <p className="text-sm text-ink-500">{t('kb.noHits')}</p>}
        <ol className="space-y-3">
          {(hits ?? []).map(h => (
            <li key={h.ref} className="rounded-xl border border-cream-200 bg-cream-50/60 p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
                <b className="text-ink-200">[{h.n}] {h.title}</b>
                {h.headingPath && <span>{h.headingPath}</span>}
                {h.page !== null && <span>{t('kb.page', { page: h.page })}</span>}
                <Badge>{h.collection}</Badge>
                <span className="ml-auto font-mono">{t('kb.ranks', { k: h.keywordRank ?? '—', s: h.semanticRank ?? '—' })}</span>
              </div>
              <p className="mt-2 line-clamp-6 whitespace-pre-wrap text-sm">{h.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <Modal open={draft !== null} title={draft?.id ? t('kb.editCollection') : t('kb.newCollection')} onClose={() => setDraft(null)}
        footer={<ModalFooter onCancel={() => setDraft(null)} onSubmit={() => void save()} disabled={!draft?.name.trim()} />}>
        {draft && <div className="space-y-3">
          <Field label={t('kb.collectionName')}>
            {id => <Input id={id} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />}
          </Field>
          <Field label={t('kb.collectionDescription')} hint={t('kb.collectionDescriptionHint')}>
            {id => <Textarea id={id} rows={3} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} />}
          </Field>
        </div>}
      </Modal>

      <Modal open={sourceModalKind !== null} title={sourceModalKind === 'gdrive' ? t('kb.addDrive') : t('kb.addUrl')} onClose={() => setSourceModalKind(null)}
        footer={<ModalFooter onCancel={() => setSourceModalKind(null)} onSubmit={() => void addSource()} busy={submittingSource} disabled={!sourceRef.trim()} />}>
        {sourceModalKind && (
          <div className="space-y-3">
            <Field label={sourceModalKind === 'gdrive' ? t('kb.driveRef') : t('kb.urlRef')} hint={sourceModalKind === 'gdrive' ? t('kb.driveHint') : t('kb.urlHint')}>
              {id => <Input id={id} value={sourceRef} placeholder={sourceModalKind === 'gdrive' ? 'https://drive.google.com/drive/folders/...' : 'https://example.com/doc.pdf'} onChange={e => setSourceRef(e.target.value)} />}
            </Field>
            <Field label={t('kb.interval')}>
              {id => (
                <Select
                  id={id}
                  value={sourceInterval}
                  onChange={setSourceInterval}
                  options={[
                    { value: '0', label: t('kb.interval_0') },
                    { value: '6', label: t('kb.interval_6') },
                    { value: '24', label: t('kb.interval_24') },
                    { value: '168', label: t('kb.interval_168') }
                  ]}
                />
              )}
            </Field>
          </div>
        )}
      </Modal>

      <Modal open={evalModalOpen} title={t('kb.evalAdd')} onClose={() => setEvalModalOpen(false)}
        footer={<ModalFooter onCancel={() => setEvalModalOpen(false)} onSubmit={() => void addEvalCase()} busy={submittingEval} disabled={!evalCaseQuery.trim() || !evalCaseDocId} />}>
        <div className="space-y-3">
          <Field label={t('kb.evalQuestion')}>
            {id => <Input id={id} value={evalCaseQuery} onChange={e => setEvalCaseQuery(e.target.value)} />}
          </Field>
          <Field label={t('kb.evalExpected')}>
            {id => (
              <Select
                id={id}
                value={evalCaseDocId}
                onChange={setEvalCaseDocId}
                options={(docs?.filter(d => d.status === 'ready') ?? []).map(d => ({ value: d.id, label: d.title }))}
              />
            )}
          </Field>
        </div>
      </Modal>
    </div>
  )
}
